/**
 * vue-i18n locale → a tag `Intl` will actually accept.
 *
 * Chatwoot's locale values are its own directory names (`app/javascript/dashboard/i18n`),
 * and four of the shipped ones are NOT BCP-47: `pt_BR`, `zh_CN`, `zh_TW`, `ur_IN` use an
 * underscore where the standard uses a hyphen. `new Intl.NumberFormat('pt_BR')` does not
 * degrade — it throws `RangeError: Incorrect locale information provided`, and a throw
 * inside a `computed` during render takes the whole section down with it. A Brazilian
 * account would have seen a white page where the credit balance should be.
 *
 * So: hyphenate, then verify. The verification is not paranoia — the locale list is
 * upstream's and grows without asking us, and the failure mode of a bad tag is a blank
 * screen rather than an ugly number.
 */

const FALLBACK = 'en';

/**
 * THE DIGITS AN ARABIC SCREEN SHOWS (spec §7 P-7.3, owner decision O-5): Arabic-Indic, produced
 * by the formatter and never typed into a string, so that a later switch to Latin digits is this
 * one value (`'latn'`) and not a sweep of the copy.
 *
 * It has to be named. CLDR 42 moved plain `ar` to Latin digits: on the Node 24 these specs run
 * on, and in current browsers, `new Intl.NumberFormat('ar').format(3)` is `3` while `ar-KW` is
 * `٣`. So every Arabic tag this returns carries the numbering as a Unicode extension
 * (`ar-u-nu-arab`), and every `Intl` formatter built from it (numbers, dates, times, relative
 * times) writes the same digits.
 */
export const ARABIC_NUMBERING = 'arab';

const withNumbering = tag =>
  tag.split('-')[0].toLowerCase() === 'ar' && !/-u-(?:.+-)?nu-/i.test(tag)
    ? `${tag}-u-nu-${ARABIC_NUMBERING}`
    : tag;

export const toIntlLocale = locale => {
  if (typeof locale !== 'string' || locale === '') return FALLBACK;
  const tag = locale.replace(/_/g, '-');
  try {
    // Cheapest available "is this tag usable?" — the same canonicaliser every Intl
    // constructor runs, without building a formatter.
    Intl.getCanonicalLocales(tag);
    return withNumbering(tag);
  } catch {
    return FALLBACK;
  }
};

export default toIntlLocale;
